import { useRef, useState } from "react";

import { ErrorSnackbar } from "@/components/ui/error-snackbar";
import {
  ExpandableNotesRow,
  type ExpandableNotesLocation,
} from "@/components/ui/expandable-notes-row";
import { MuiBasicTable, MuiBasicTableBody } from "@/components/ui/mui-basic-table";
import { NotesDialog } from "@/components/ui/notes-dialog";
import { useUpdateSpecLineLocationNotes } from "@/hooks/spec-editor-locations";
import type { SpecEditorTarget } from "@/hooks/spec-editor-target";
import { useSpecLinesWriteStamp, type SpecLinesLine } from "@/hooks/specifications";
import type { NotesTableChanges } from "@/lib/spec-base-changes";
import { ERRORS } from "@/lib/spec-editor-errors";

// The Editor's per-location notes table (#808) — the donor's
// `LineWithLocationsNotes` (`SpecEditIsland/components/MainContent/Lines/
// LineNotes/LineWithLocationsNotes.tsx`): under every `specific` line, the
// shared expandable notes row over the line's note and its placements, and
// the shared notes dialog a room's `Add a note` or ⋮ `Edit` opens. #810 mounts
// it under the line row; #809 decides which lines get it and hands it the
// row's class names and selected state.
//
// - **The rooms are the line's placements as the cache holds them**, every one
//   in the read's order — the donor reads `lineLocationIds` unfiltered — so a
//   delete or a rename elsewhere reaches this table through the one cache
//   updater (ADR-0014 decision 1) and nothing here keeps a copy.
// - **The dialog is held for one placement** — `Edit note` / `Save` when it
//   has a note, else `Add a note` / `Add note` — and seeded with its note.
//   Closing clears the held placement, so the title and the submit label
//   follow the emptied value during the exit (carried, Assumption 10).
// - **The save waits for the server** (the selections rulings item 11; a
//   ruled break from the donor's close-at-once): Cancel and the submit are
//   held while it runs, a success closes the dialog, and a failure leaves it
//   open over the text with `UPDATE_LINE_LOCATION_NOTE` on the snackbar. The
//   X, Escape and the backdrop still close it mid-save, as the donor's do —
//   and another room's dialog opened meanwhile is neither held nor closed by
//   the first room's save.
// - **Any write to the version's lines re-opens a folded table** over more
//   than one room — another line's toggle, a rename, a page scrolled in — as
//   the donor's does: its `createSelector` over the whole object cache hands
//   the row a fresh rooms array on every write (`objectCache.ts:516-522`). A
//   refetch is not a write here (ADR-0014 decision 1).
// - A note saved for one line's room touches no other line's placement of
//   that room: the write names the placement.
//
// No `deleted` gate: a struck line's controls stay live, as the donor's do
// (#809 decides whether to mount the table on a struck line).

/** `paddingLeftDesktop={8}` (`LineWithLocationsNotes.tsx:51`). */
const CONTENT_INSET = 8;

interface DialogState {
  open: boolean;
  placement: ExpandableNotesLocation | null;
  /** Which opening of the dialog this is: a save belongs to the one it began in. */
  session: number;
}

export interface SpecLineLocationNotesProps {
  /** The line as the cached lines page holds it. */
  line: SpecLinesLine;
  target: SpecEditorTarget;
  /** The row model's classes for this row (#809). */
  className?: string;
  /** The row's Active-selection state (#809). */
  selected?: boolean;
  /**
   * The notes that differ from the base, drawn beside the new with "Show
   * changes from Base" on (#1104; #1060 Q9); unset draws none.
   */
  notesChanges?: NotesTableChanges;
}

export function SpecLineLocationNotes({
  line,
  target,
  className,
  selected = false,
  notesChanges,
}: SpecLineLocationNotesProps) {
  const update = useUpdateSpecLineLocationNotes();
  const linesWritten = useSpecLinesWriteStamp(target);
  const [dialog, setDialog] = useState<DialogState>({ open: false, placement: null, session: 0 });
  const sessions = useRef(0);
  // The opening whose save is in flight: the hold and the close on success are
  // that opening's alone, so a dialog dismissed mid-save leaves the next one —
  // another room's, or the same room's opened again — untouched.
  const [savingFor, setSavingFor] = useState<number | null>(null);
  // Every failed save shows the message — the one hook's `isError` is only
  // its latest call's, so a dismissed save failing after the next began
  // would show none. A new save takes it down first, as the hook's would.
  const [saveFailure, setSaveFailure] = useState<number | null>(null);
  const failures = useRef(0);
  const close = () => setDialog((current) => ({ ...current, open: false, placement: null }));
  const currentNotes = dialog.placement?.notes || "";

  return (
    <>
      <MuiBasicTable>
        <MuiBasicTableBody>
          <ExpandableNotesRow
            className={className}
            paddingLeftDesktop={CONTENT_INSET}
            reopenKey={linesWritten}
            item={{
              id: line.id,
              notes: line.notes,
              status: line.status,
              locations: line.locations,
            }}
            onLocationNote={(_lineId, placement) =>
              setDialog({ open: true, placement, session: ++sessions.current })
            }
            selected={selected}
            notesChanges={notesChanges}
          />
        </MuiBasicTableBody>
      </MuiBasicTable>

      <NotesDialog
        open={dialog.open}
        onOpenChange={(open) => {
          if (!open) close();
        }}
        edit={currentNotes !== ""}
        seed={currentNotes}
        saving={savingFor === dialog.session}
        onSubmit={(notes) => {
          const placement = dialog.placement;
          if (!placement) return;
          const session = dialog.session;
          setSavingFor(session);
          setSaveFailure(null);
          const settle = () => setSavingFor((current) => (current === session ? null : current));
          // The write's own promise rather than `mutate`'s per-call
          // `onSuccess`, which a newer `mutate` would silence.
          update
            .mutateAsync({ ...target(), lineId: line.id, lineLocationId: placement.id, notes })
            .then(
              () => {
                settle();
                // Only the opening the save began in.
                setDialog((current) =>
                  current.session === session
                    ? { ...current, open: false, placement: null }
                    : current,
                );
              },
              () => {
                settle();
                setSaveFailure(++failures.current);
              },
            );
        }}
      />

      <ErrorSnackbar
        key={saveFailure ?? "none"}
        open={saveFailure !== null}
        message={ERRORS.UPDATE_LINE_LOCATION_NOTE}
        onClose={() => setSaveFailure(null)}
      />
    </>
  );
}
